import type {
  FindingResponse,
  FindingResultPaginatedResponse,
  FixResultEmbedded,
} from '../src/utils/api-client/api-schemas'

/**
 * Helper to create a patch paginated response for embedding in fix results.
 */
const createPatchEmbedded = (state: 'in-progress' | 'completed-success' | 'completed-failed'): FixResultEmbedded => ({
  'latest-patch': {
    _links: { first: { href: '#' }, self: { href: '#' } },
    _embedded: {
      items: [
        {
          type: 'github',
          id: `patch-${state}`,
          state,
          html_url: 'https://github.com/example/repo/pull/123',
          pr_number: 123,
          _links: { self: { href: '#' } },
        },
      ],
    },
    page: { number: 0, size: 10 },
    total: 1,
  },
})

/**
 * Extract representative results from all results (at most 1 triage + 1 fix).
 * Used to compute the representative-results embedded in a finding response.
 */
export const getRepresentativeResults = (
  allResults: NonNullable<NonNullable<FindingResponse['_embedded']>['representative-results']>['_embedded']['items']
): NonNullable<NonNullable<FindingResponse['_embedded']>['representative-results']>['_embedded']['items'] => {
  const firstTriage = allResults.find(r => r.type === 'triage')
  const firstFix = allResults.find(r => r.type === 'fix')

  return [firstTriage, firstFix].filter(result => !!result)
}

export type FindingPattern = {
  title: string
  rule: string
  severity: FindingResponse['severity']
  results: NonNullable<NonNullable<FindingResponse['_embedded']>['representative-results']>['_embedded']['items']
}

/**
 * Get finding patterns for a given finding ID.
 * `results` contains ALL results (full history), representative-results will be computed from these.
 */
export const getFindingPatterns = (findingId: string): FindingPattern[] => [
  {
    title: 'SQL Injection vulnerability in UserController.java',
    rule: 'java:S3649',
    severity: { label: 'critical', rank: 9.0 },
    results: [
      {
        id: `triage-${findingId}-0`,
        type: 'triage',
        completed: '2024-01-15T10:00:00Z',
        outcome: {
          type: 'completed-triage',
          suggested_status: 'true_positive',
          suggested_severity: { label: 'critical', rank: 9.0 },
          summary: 'SQL injection confirmed - user input directly concatenated into query',
        },
        _links: {
          self: { href: `/api/v1/triage-results/triage-${findingId}-0` },
          finding: { href: `/api/v1/scans/scan-id/findings/${findingId}` },
          analysis: { href: '/api/v1/analyses/analysis-0' },
        },
      },
      {
        id: `fix-${findingId}-1`,
        type: 'fix',
        completed: '2024-01-15T11:00:00Z',
        outcome: {
          type: 'completed-fix',
          strategy: 'ai',
          confidence: 'high',
          summary: 'Replace string concatenation with parameterized query',
          description:
            'This fix replaces the vulnerable SQL string concatenation with a parameterized query using PreparedStatement. The user input is now passed as a parameter rather than being directly concatenated into the SQL string, preventing SQL injection attacks.',
          quality: {
            safety: {
              score: 95,
              description: 'The fix correctly parameterizes all user inputs without changing query behavior.',
            },
            effectiveness: { score: 90, description: 'Fully addresses the SQL injection vulnerability.' },
            cleanliness: { score: 85, description: 'Code follows Java best practices for database access.' },
            merge_readiness: { score: 90, description: 'Fix is complete and ready to merge.' },
          },
        },
        _links: {
          self: { href: `/api/v1/fix-results/fix-${findingId}-1` },
          finding: { href: `/api/v1/scans/scan-id/findings/${findingId}` },
          analysis: { href: '/api/v1/analyses/analysis-0' },
          changesets: { href: `/api/v1/fix-results/fix-${findingId}-1/changesets` },
          patches: { href: '#' },
          'latest-patch': { href: '#' },
        },
      },
      {
        id: `triage-${findingId}-2`,
        type: 'triage',
        completed: '2024-01-15T12:00:00Z',
        outcome: {
          type: 'completed-triage',
          suggested_status: 'suspicious',
          suggested_severity: { label: 'high', rank: 8.0 },
          summary: 'Previous analysis flagged as suspicious',
        },
        _links: {
          self: { href: `/api/v1/triage-results/triage-${findingId}-2` },
          finding: { href: `/api/v1/scans/scan-id/findings/${findingId}` },
          analysis: { href: '/api/v1/analyses/analysis-1' },
        },
      },
      {
        id: `fix-${findingId}-3`,
        type: 'fix',
        completed: '2024-01-15T13:00:00Z',
        outcome: { type: 'failed-fix', problem: { code: 'FIX_FAILED', message: 'Could not generate fix' } },
        _links: {
          self: { href: `/api/v1/fix-results/fix-${findingId}-3` },
          finding: { href: `/api/v1/scans/scan-id/findings/${findingId}` },
          analysis: { href: '/api/v1/analyses/analysis-1' },
          changesets: { href: `/api/v1/fix-results/fix-${findingId}-3/changesets` },
          patches: { href: '#' },
          'latest-patch': { href: '#' },
        },
      },
    ],
  },
  {
    title: 'Hardcoded credentials in config file',
    rule: 'java:S2068',
    severity: { label: 'high', rank: 8.0 },
    results: [
      {
        id: `triage-${findingId}-0`,
        type: 'triage',
        completed: '2024-01-15T10:00:00Z',
        outcome: {
          type: 'completed-triage',
          suggested_status: 'true_positive',
          suggested_severity: { label: 'high', rank: 8.0 },
          summary: 'Hardcoded password detected in configuration',
        },
        _links: {
          self: { href: `/api/v1/triage-results/triage-${findingId}-0` },
          finding: { href: `/api/v1/scans/scan-id/findings/${findingId}` },
          analysis: { href: '/api/v1/analyses/analysis-0' },
        },
      },
      {
        id: `fix-${findingId}-1`,
        type: 'fix',
        completed: '2024-01-15T11:00:00Z',
        outcome: { type: 'completed-fix', strategy: 'ai', confidence: 'high' },
        _links: {
          self: { href: `/api/v1/fix-results/fix-${findingId}-1` },
          finding: { href: `/api/v1/scans/scan-id/findings/${findingId}` },
          analysis: { href: '/api/v1/analyses/analysis-0' },
          changesets: { href: `/api/v1/fix-results/fix-${findingId}-1/changesets` },
          patches: { href: '#' },
          'latest-patch': { href: '#' },
        },
        _embedded: createPatchEmbedded('in-progress'),
      },
    ],
  },
  {
    title: 'Cross-Site Scripting (XSS) in template rendering',
    rule: 'java:S5131',
    severity: { label: 'high', rank: 8.0 },
    results: [
      {
        id: `triage-${findingId}-0`,
        type: 'triage',
        completed: '2024-01-15T10:00:00Z',
        outcome: {
          type: 'completed-triage',
          suggested_status: 'true_positive',
          suggested_severity: { label: 'high', rank: 8.0 },
          summary: 'XSS vulnerability confirmed in template output',
        },
        _links: {
          self: { href: `/api/v1/triage-results/triage-${findingId}-0` },
          finding: { href: `/api/v1/scans/scan-id/findings/${findingId}` },
          analysis: { href: '/api/v1/analyses/analysis-0' },
        },
      },
      {
        id: `fix-${findingId}-1`,
        type: 'fix',
        completed: '2024-01-15T11:00:00Z',
        outcome: {
          type: 'completed-fix',
          strategy: 'ai',
          confidence: 'medium',
          summary: 'Apply HTML encoding to template output',
          description:
            'This fix adds HTML encoding to the template output using a context-aware escaping function. User-controlled data is now sanitized before being rendered in the HTML context, preventing XSS attacks.',
          quality: {
            safety: {
              score: 88,
              description: 'The fix applies proper HTML encoding without breaking legitimate functionality.',
            },
            effectiveness: { score: 85, description: 'Addresses the XSS vulnerability in the identified location.' },
            cleanliness: { score: 80, description: 'Code follows template security best practices.' },
            merge_readiness: { score: 85, description: 'Fix is complete and ready to merge.' },
          },
        },
        _links: {
          self: { href: `/api/v1/fix-results/fix-${findingId}-1` },
          finding: { href: `/api/v1/scans/scan-id/findings/${findingId}` },
          analysis: { href: '/api/v1/analyses/analysis-0' },
          changesets: { href: `/api/v1/fix-results/fix-${findingId}-1/changesets` },
          patches: { href: '#' },
          'latest-patch': { href: '#' },
        },
        _embedded: createPatchEmbedded('completed-success'),
      },
    ],
  },
  {
    title: 'Potential path traversal vulnerability',
    rule: 'java:S2083',
    severity: { label: 'medium', rank: 6.0 },
    results: [],
  },
  {
    title: 'Insecure deserialization detected',
    rule: 'java:S5135',
    severity: { label: 'critical', rank: 9.0 },
    results: [
      {
        id: `triage-${findingId}-0`,
        type: 'triage',
        completed: '2024-01-15T10:00:00Z',
        outcome: {
          type: 'completed-triage',
          suggested_status: 'suspicious',
          suggested_severity: { label: 'critical', rank: 9.0 },
          summary: 'Deserialization of untrusted data detected',
        },
        _links: {
          self: { href: `/api/v1/triage-results/triage-${findingId}-0` },
          finding: { href: `/api/v1/scans/scan-id/findings/${findingId}` },
          analysis: { href: '/api/v1/analyses/analysis-0' },
        },
      },
      {
        id: `triage-${findingId}-1`,
        type: 'triage',
        completed: '2024-01-15T11:00:00Z',
        outcome: {
          type: 'completed-triage',
          suggested_status: 'true_positive',
          suggested_severity: { label: 'critical', rank: 9.0 },
          summary: 'Confirmed after manual review',
        },
        _links: {
          self: { href: `/api/v1/triage-results/triage-${findingId}-1` },
          finding: { href: `/api/v1/scans/scan-id/findings/${findingId}` },
          analysis: { href: '/api/v1/analyses/analysis-1' },
        },
      },
      {
        id: `fix-${findingId}-2`,
        type: 'fix',
        completed: '2024-01-15T12:00:00Z',
        outcome: {
          type: 'completed-fix',
          strategy: 'ai',
          confidence: 'low',
          summary: 'Replace ObjectInputStream with safe deserialization',
          description:
            'This fix replaces the unsafe ObjectInputStream deserialization with a safer alternative that validates the class types before deserialization. A whitelist of allowed classes is enforced to prevent arbitrary code execution.',
          quality: {
            safety: {
              score: 70,
              description: 'The fix limits deserialization to whitelisted classes, but manual review is recommended.',
            },
            effectiveness: {
              score: 65,
              description: 'Mitigates the vulnerability but may require configuration adjustments.',
            },
            cleanliness: { score: 75, description: 'Code structure is reasonable but adds complexity.' },
            merge_readiness: { score: 70, description: 'Fix requires developer review before merging.' },
          },
        },
        _links: {
          self: { href: `/api/v1/fix-results/fix-${findingId}-2` },
          finding: { href: `/api/v1/scans/scan-id/findings/${findingId}` },
          analysis: { href: '/api/v1/analyses/analysis-1' },
          changesets: { href: `/api/v1/fix-results/fix-${findingId}-2/changesets` },
          patches: { href: '#' },
          'latest-patch': { href: '#' },
        },
        _embedded: createPatchEmbedded('completed-failed'),
      },
    ],
  },
]

/**
 * Simple string hash function for deterministic pattern selection.
 * Returns a positive integer hash of the input string.
 */
const hashString = (str: string): number => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }
  return Math.abs(hash)
}

/**
 * Helper to generate findings with multiple analysis results for dev:mocks.
 * Returns a deterministic finding based on the finding ID to ensure consistent test data.
 * Uses the same hash-based pattern selection as generateFullResultsForFinding to ensure
 * the finding's embedded representative-results match the full results from the results endpoint.
 *
 * Now includes embedded `results` containing all results (full history) in addition to
 * `representative-results` (subset for display). This eliminates N+1 API calls.
 */
export const generateFindingWithMultipleResults = (findingId: string): FindingResponse => {
  const patterns = getFindingPatterns(findingId)
  const patternIndex = hashString(findingId) % patterns.length
  const pattern = patterns[patternIndex]

  if (!pattern) {
    return {
      id: findingId,
      title: 'Sample finding',
      rule: 'sample-rule',
      severity: { label: 'medium', rank: 6.0 },
      html_url: `https://example.com/findings/${findingId}`,
      _links: {
        self: { href: `/api/v1/scans/scan-id/findings/${findingId}` },
        results: { href: `/api/v1/scans/scan-id/findings/${findingId}/results` },
        'representative-results': { href: `/api/v1/scans/scan-id/findings/${findingId}/results?representative=true` },
      },
      _embedded: {
        'representative-results': {
          _links: {
            first: { href: `/api/v1/scans/scan-id/findings/${findingId}/results?representative=true` },
            self: { href: `/api/v1/scans/scan-id/findings/${findingId}/results?representative=true` },
            finding: { href: '#' },
          },
          _embedded: { items: [] },
          page: { number: 0, size: 10 },
          total: 0,
        },
        results: {
          _links: {
            first: { href: `/api/v1/scans/scan-id/findings/${findingId}/results` },
            self: { href: `/api/v1/scans/scan-id/findings/${findingId}/results` },
            finding: { href: '#' },
          },
          _embedded: { items: [] },
          page: { number: 0, size: 100 },
          total: 0,
        },
      },
    }
  }

  const representativeResults = getRepresentativeResults(pattern.results)
  const allResults = pattern.results

  return {
    id: findingId,
    title: pattern.title,
    rule: pattern.rule,
    severity: pattern.severity,
    html_url: `https://example.com/findings/${findingId}`,
    _links: {
      self: { href: `/api/v1/scans/scan-id/findings/${findingId}` },
      results: { href: `/api/v1/scans/scan-id/findings/${findingId}/results` },
      'representative-results': { href: `/api/v1/scans/scan-id/findings/${findingId}/results?representative=true` },
    },
    _embedded: {
      'representative-results': {
        _links: {
          first: { href: `/api/v1/scans/scan-id/findings/${findingId}/results?representative=true` },
          self: { href: `/api/v1/scans/scan-id/findings/${findingId}/results?representative=true` },
          finding: { href: '#' },
        },
        _embedded: { items: representativeResults },
        page: { number: 0, size: 10 },
        total: representativeResults.length,
      },
      // Full results embedded for expandable rows - eliminates N+1 queries
      results: {
        _links: {
          first: { href: `/api/v1/scans/scan-id/findings/${findingId}/results` },
          self: { href: `/api/v1/scans/scan-id/findings/${findingId}/results` },
          finding: { href: '#' },
        },
        _embedded: { items: allResults },
        page: { number: 0, size: 100 },
        total: allResults.length,
      },
    },
  }
}

/**
 * Generate full results for a finding.
 * Returns all results from pattern.results (representative-results is a subset of these).
 */
export const generateFullResultsForFinding = (findingId: string): FindingResultPaginatedResponse => {
  const patterns = getFindingPatterns(findingId)
  const patternIndex = hashString(findingId) % patterns.length
  const pattern = patterns[patternIndex]
  const allResults = pattern?.results ?? []

  return {
    _links: {
      first: { href: `/api/v1/scans/scan-id/findings/${findingId}/results?representative=true` },
      self: { href: `/api/v1/scans/scan-id/findings/${findingId}/results?representative=true` },
      finding: { href: '#' },
    },
    _embedded: { items: allResults },
    page: { number: 0, size: 100 },
    total: allResults.length,
  }
}
